import { useCallback, useContext, useEffect, useState } from "react";

import { GroupConstants } from "../../../../../../../../constants";
import { SelectData } from "../../../../../../../../models";
import { addOrRemoveProjectsFromGroup } from "../../../../../../../../service/project";
import { searchProjects } from "../../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../../service/Shared";
import { useAuth } from "../../../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../../../utils";
import { Toast } from "../../../../../../../../widget";
import { GroupContext } from "../../GroupContext";

interface UseAddGroupProjectModalProps {
  closeModal: () => void;
  isLandholdingGroup?: boolean;
}

interface UseAddGroupProjectModalReturnData {
  projects: SelectData;
  errors: ServiceError[];
  addProject: () => void;
  onCancel: () => void;
  fetchProjects: (projectDisplayName: string) => void;
  setProjectUuid: (projectUuid: string) => void;
  isAddProjectLoading: boolean;
}

export const useAddGroupProjectModal = ({
  closeModal,
  isLandholdingGroup = false,
}: UseAddGroupProjectModalProps): UseAddGroupProjectModalReturnData => {
  const { currentDeveloperUuid } = useAuth();
  const { groupDetails, refreshGroup, setRefreshGroup } = useContext(GroupContext);

  const [status, setStatus] = useState<string>();
  const [standardUuid, setStandardUuid] = useState<string>();
  const [validatorUuid, setValidatorUuid] = useState<string>();
  const [projects, setProjects] = useState<SelectData>([]);
  const [projectUuid, setProjectUuid] = useState<string>();
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const [isAddProjectLoading, setIsAddProjectLoading] = useState(false);

  const fetchProjects = useCallback(
    async (projectDisplayName: string) => {
      if (isLandholdingGroup) {
        await searchProjects({
          paging: {
            limit: 50,
            beforeCursor: null,
            afterCursor: null,
          },
          sort: [{ key: "results.displayName", direction: "asc" }],
          filter: {
            results: {
              displayName: {
                operator: "contains",
                value: projectDisplayName,
              },
            },
          },
        }).then((response) => {
          const data = response.data?.results
            ?.filter((project) => project.groups.length === 0)
            .map((project) => ({
              key: project.uuid,
              value: project.displayName,
            }));

          setProjects(data || []);
        });
      } else if (currentDeveloperUuid && status && standardUuid) {
        await searchProjects({
          paging: {
            limit: 50,
            beforeCursor: null,
            afterCursor: null,
          },
          sort: [{ key: "results.displayName", direction: "asc" }],
          filter: {
            results: {
              developer: {
                uuid: {
                  operator: "eq",
                  value: currentDeveloperUuid,
                },
              },
              standard: {
                uuid: {
                  operator: "eq",
                  value: standardUuid,
                },
              },
              displayName: {
                operator: "contains",
                value: projectDisplayName,
              },
              status: {
                operator: "eq",
                value: status,
              },
              validator: validatorUuid
                ? {
                    uuid: {
                      operator: "eq",
                      value: validatorUuid,
                    },
                  }
                : {
                    uuid: undefined,
                  },
            },
          },
        }).then((response) => {
          const notGroupProjects = response.data?.results?.filter((project) => project.groups.length === 0);

          const data = validatorUuid
            ? notGroupProjects?.map((project) => ({
                key: project.uuid,
                value: project.displayName,
              }))
            : notGroupProjects
                ?.filter((project) => project.validator.uuid === null)
                .map((project) => ({
                  key: project.uuid,
                  value: project.displayName,
                }));

          setProjects(data || []);
        });
      }
    },
    [isLandholdingGroup, status]
  );

  const onCancel = (): void => {
    setErrors([]);
    setProjectUuid(undefined);
    fetchProjects("");
    closeModal();
  };

  const addProject = useIsLoadingWrapper(async (): Promise<void> => {
    if (!projectUuid) {
      setErrors([
        {
          code: "NO_PROJECT",
          message: "A project must be selected",
        },
      ]);
    } else if (!groupDetails?.groupGuid) {
      setErrors([
        {
          code: "NO_GROUP",
          message: "No group provided",
        },
      ]);
    } else {
      await addOrRemoveProjectsFromGroup({
        projectUuid,
        groupUuid: groupDetails?.groupGuid,
        operation: GroupConstants.ADD_PROJECT_OPERATION,
      }).then((response) => {
        if (response.status === Status.Success) {
          Toast.success({ message: "Project added to group successfully" });
          setRefreshGroup(!refreshGroup);

          onCancel();
        }
        if (response.status === Status.Error && response.errors) {
          setErrors(response.errors);
        }
      });
    }
  }, setIsAddProjectLoading);

  useEffect(() => {
    // Validator & status must be the same between all projects in a group
    setStatus(groupDetails?.projects[0]?.status);
    setStandardUuid(groupDetails?.projects[0]?.standard.uuid);
    setValidatorUuid(groupDetails?.projects[0]?.validator?.uuid);
    fetchProjects("");
  }, [refreshGroup, status]);

  return {
    projects,
    errors,
    addProject,
    onCancel,
    fetchProjects,
    setProjectUuid,
    isAddProjectLoading,
  };
};
